/**
 * Host context for campaign wizards (#6232). The /new routes render the
 * wizards full-page, where leaving the flow means router navigation. When a
 * wizard is instead hosted inside CampaignWizardModal, navigating would tear
 * the user away from the page under the modal, so the host registers itself
 * here and the wizards route their two exit points through it:
 *
 *  - BACK on the first step  -> host.onExit()     (chooser or close)
 *  - successful completion   -> host.onFinished() (close, stay on the page)
 *
 * A null context (the full-page routes) keeps the original navigate calls.
 */

import { createContext, useContext } from 'react';

export interface WizardHost {
  /** BACK pressed on the wizard's first step. */
  onExit: () => void;
  /** Campaign(s) created successfully; generation continues in Celery.
   * Wizards pass the created campaign ids when they have them so the host
   * can stamp provenance (e.g. mark the source call action done). */
  onFinished: (result?: { campaignIds?: number[] }) => void;
  /**
   * The wizard is about to navigate the app somewhere else entirely (e.g. an
   * empty state pointing at /business). The host closes so the destination
   * page isn't buried under a stale dialog; the caller still navigates.
   */
  onLeave: () => void;
  /**
   * The type picker (SelectCampaignType) resolved a Google pick to a wizard
   * route; the host mounts that wizard in place instead of navigating.
   */
  openWizard: (to: string) => void;
}

export const WizardHostContext = createContext<WizardHost | null>(null);

export const useWizardHost = () => useContext(WizardHostContext);
